import { Recipe } from '../types/mealPlan';

/**
 * Imports and remixes land in the cookbook asynchronously, wherever the
 * user's sort and persisted filters put them (by default alphabetically,
 * possibly pages away or filtered out). The cookbook tracks recipes that
 * arrived while the user was watching and pins them to the top until
 * dismissed, so a finished import or remix is always findable.
 */

/** In-flight remixes sit in the recipe list but aren't shown until done. */
export const isCookbookDisplayable = (recipe: Recipe): boolean =>
  recipe.remixStatus?.status !== 'pending' && recipe.remixStatus?.status !== 'processing';

const isImportOrRemix = (recipe: Recipe): boolean =>
  recipe.remixStatus ? recipe.remixStatus.status === 'completed' : !!recipe.importStatus;

/**
 * Ids of imported or remixed recipes that are displayable in `recipes` but
 * weren't in `shownIds` (the ids the cookbook has already displayed) — i.e.
 * imports and remixes that finished since the last refresh. Manually created
 * recipes (neither status) and failed remixes are excluded.
 */
export const findNewlyAddedRecipeIds = (
  recipes: Recipe[],
  shownIds: ReadonlySet<string>
): string[] =>
  recipes
    .filter((r) => !shownIds.has(r.id) && isCookbookDisplayable(r) && isImportOrRemix(r))
    .map((r) => r.id);

/** Prepend `landed` to `current` (newest first), without duplicates. */
export const mergeNewlyAddedIds = (current: string[], landed: string[]): string[] =>
  landed.length === 0
    ? current
    : [...landed, ...current.filter((id) => !landed.includes(id))];

/**
 * Move pinned recipes to the front, ordered as in `pinnedIds` (newest
 * first); the rest keep their order.
 */
export const pinRecipesToTop = (recipes: Recipe[], pinnedIds: string[]): Recipe[] => {
  if (pinnedIds.length === 0) return recipes;
  const rank = new Map(pinnedIds.map((id, i) => [id, i]));
  const pinned = recipes
    .filter((r) => rank.has(r.id))
    .sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
  return [...pinned, ...recipes.filter((r) => !rank.has(r.id))];
};

/** Banner copy announcing pinned recipes. */
export const describeNewlyAdded = (recipes: Recipe[]): string => {
  const remixes = recipes.filter((r) => r.remixStatus).length;
  const suffix = 'pinned to the top of your cookbook.';
  if (recipes.length === 1) {
    const name = recipes[0].name;
    return remixes
      ? `Your remix "${name}" is ready and ${suffix}`
      : `"${name}" was imported and is ${suffix}`;
  }
  if (remixes === recipes.length) return `${recipes.length} remixes are ready and ${suffix}`;
  if (remixes === 0) return `${recipes.length} recipes were imported and are ${suffix}`;
  return `${recipes.length} new recipes are ${suffix}`;
};
